'use client';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CardMedia,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ProviderImg from '@/assets/img/provider.png';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';

import {
  arrayToCommaDelimatedString,
  configurationDataLogic,
  priceNumberFormat,
  singleProductEncodeSearchParams,
} from '@/lib/utils';
import ProductLibraryModal from './modal/productLibraryModal';
import { useTranslation } from 'react-i18next';
import { MixpanelTracking } from '@/services/mixpanel';
import { MixpanelEventHub } from '@/services/eventEnums';
import { isPlatformExplore, isPlatformHub } from '@/constants';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useShoppingCart } from '@/context/ShoppingCartContext';
import Linkify from 'linkify-react';
import DownloadSampleDialog from './modal/downloadSampleDialog';
import K from '@/constants/constants';
interface Configuration {
  name: string;
  id: string;
  parameters: Record<string, any>;
  is_standard?: boolean;
  properties?: {
    bands?: string[];
    data?: any;
  };
}

interface Offering {
  offering_id: string;
  offering_name: string;
  configurations: Configuration[];
  price?: number;
  minimum_billable_aoi_size?: number;
  maximum_platform_aoi_size?: number;
  on_platform: boolean;
  supported_applications?: string[];
  sensor_type?: string;
  price_type?: string;
  sensor_mode?: string;
}

interface ProviderData {
  partner_name: string;
  product_name: string;
  product_id: string;
  sensor_type?: string;
  sensor_mode?: string;
  resolution: number;
  bandsList?: string;
  summary?: string;
  archiveOfferings?: Offering[];
  taskingOfferings?: Offering[];
  properties?: {
    samples?: string[];
    sample_preview?: string;
  };
}

interface ProviderDetailTabProps {
  data: ProviderData;
  type: 'Archive' | 'Tasking';
  isUserLoggedIn: boolean;
}

interface QuickAction {
  value: string;
  label: string;
  icon: React.ReactNode;
  url: string;
  key: string;
  eventName: string;
}

interface priceFormatObject {
  [key: string]: string;
}
//update the price format here
const priceFormat: priceFormatObject = {
  price_per_km2: 'per sq. km',
};

function ProviderDetailTab({ data, type, isUserLoggedIn }: ProviderDetailTabProps) {
  const [offeringData, setOfferingData] = useState<Offering | null>(null);
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [selectOffering, setSelectOffering] = useState<string>('');
  const { feasibilityRequestData, setFeasibilityRequestData } = useShoppingCart();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { t } = useTranslation('provider');

  const offerings = useMemo(
    () => (type === 'Archive' ? data?.archiveOfferings : data?.taskingOfferings) ?? [],
    [data, type]
  );

  const offeringOptions = useMemo(
    () =>
      offerings?.map(({ offering_name, offering_id }) => ({
        name: offering_name,
        value: offering_id,
      })) ?? [],
    [offerings]
  );

  const getOfferingIdFromSearchParams = () => {
    const searchParamsUrl = searchParams.toString();
    let updatedUrl = '';

    if (searchParamsUrl.includes('&nav=false')) {
      updatedUrl = searchParamsUrl.replace('&nav=false', '');
    } else if (searchParamsUrl.includes('nav=false')) {
      updatedUrl = searchParamsUrl.replace('nav=false', '');
    } else {
      updatedUrl = searchParamsUrl;
    }

    updatedUrl = updatedUrl.endsWith('=') ? updatedUrl.slice(0, -1) : updatedUrl;
    const decodedSearchparamsUrl = decodeURIComponent(updatedUrl);
    const params = new URLSearchParams(decodedSearchparamsUrl);
    const decodedOfferingIdParam = params.get('offeringId');
    return decodedOfferingIdParam;
  };

  useEffect(() => {
    if (offerings?.length > 1) {
      const decodedOfferingIdParam = getOfferingIdFromSearchParams();
      if (decodedOfferingIdParam) {
        const allOfferings = type === 'Archive' ? data?.archiveOfferings : data?.taskingOfferings;
        const selectedOffering: any = allOfferings?.find(
          item => item?.offering_id === decodedOfferingIdParam
        );
        if (selectedOffering) {
          setSelectOffering(decodedOfferingIdParam);
          setOfferingData(selectedOffering);
          setConfigurations(selectedOffering?.configurations ?? []);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    const decodedOfferingIdParam = getOfferingIdFromSearchParams();
    if (offerings?.length === 1) {
      setOfferingData(offerings[0]);
      setConfigurations(offerings[0]?.configurations ?? []);
    }
    if (offerings?.length > 1 && !decodedOfferingIdParam) {
      setOfferingData(null);
      setSelectOffering('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerings]);

  const handleOfferingChange = (e: SelectChangeEvent<string>) => {
    const id = e.target.value;
    const typeForUrlParams = type === 'Archive' ? 'archive' : 'tasking';
    const urlParams = singleProductEncodeSearchParams(id, typeForUrlParams);
    window.history.pushState(null, '', pathname + '?' + urlParams);
    setSelectOffering(id);
    const allOfferings = type === 'Archive' ? data?.archiveOfferings : data?.taskingOfferings;
    const selectedOffering = allOfferings?.find(item => item?.offering_id === id);
    if (selectedOffering) {
      setOfferingData(selectedOffering);
      setConfigurations(selectedOffering?.configurations ?? []);
      MixpanelTracking.getInstance().track(MixpanelEventHub.PROVIDER_PRODUCT_OFFERING, {
        product_name: `${data?.partner_name} - ${data?.product_name}`,
        offering_name: selectedOffering.offering_name,
      });
    }
  };

  function getPriceFormat(priceType: string): string {
    //the object is above
    return priceFormat[priceType] ? priceFormat[priceType] : '';
  }

  const actions: QuickAction[] = [
    {
      value: 'download-samples',
      label: 'Download Samples',
      icon: <FileDownloadOutlinedIcon />,
      url: data?.properties?.samples?.[0] ?? '',
      key: 'downloadSamples',
      eventName: MixpanelEventHub.PROVIDER_PRODUCT_DOWNLOAD_SAMPLES_CLICK,
    },
    {
      value: 'contact-support',
      label: 'Contact Support',
      icon: <ContactSupportOutlinedIcon />,
      url: 'mailto:support@skywatch.com',
      key: 'contactSupport',
      eventName: MixpanelEventHub.PROVIDER_CONTACT_SUPPORT_CLICK,
    },
  ];

  const configurationDataCheck = configurations?.filter(
    item => item?.name !== 'Default Configuration'
  );
  const configurationData = configurationDataCheck?.map(item => item?.parameters);
  const updatedConfigurationData =
    configurationData?.length > 0 ? configurationDataLogic(configurationData, true) : null;

  const isVantor = data?.product_name?.includes(K.LABELS.SOURCES.VANTOR) ?? false;
  const bandList = isVantor
    ? arrayToCommaDelimatedString(offeringData?.configurations?.[0]?.properties?.bands)
    : data?.bandsList;

  const isProductOrderAbleForHub =
    offeringData?.on_platform === true && offeringData?.supported_applications?.includes('hub')
      ? true
      : false;

  const isButtonVisible =
    process.env.NEXT_PUBLIC_SHOW_PROVIDER_ORDER_BUTTON &&
    process.env.NEXT_PUBLIC_SHOW_PROVIDER_ORDER_BUTTON === 'true' &&
    offeringData
      ? true
      : false;

  const isProductOrderAbleForExplore =
    isButtonVisible &&
    offeringData?.on_platform === true &&
    offeringData?.supported_applications?.includes('explore')
      ? true
      : false;

  const actionButton = (item: QuickAction) => {
    const { value, url, icon, key, eventName } = item;
    const downloadSamplePropData = {
      isUserLoggedIn,
      data,
      value,
      url,
      icon,
      key,
      eventName,
    };
    if (key === 'downloadSamples')
      return <DownloadSampleDialog downloadSamplePropData={downloadSamplePropData} />;
    return (
      <ListItem key={value}>
        <ListItemButton
          className="quick-action-list-btn"
          aria-label={key}
          component="a"
          href={url}
          onClick={() => {
            {
              MixpanelTracking.getInstance().track(eventName, {
                product_name: `${data?.partner_name} - ${data?.product_name}`,
              });
            }
          }}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText primary={t(key)} />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box className="provider-detail-wrapper">
      <Box className="provider-detail-content ">
        <Box className="provider-product-detail tech-card">
          <Typography variant="h6">{t('technicalSpecifications')}</Typography>
          <ul className="detail-list" aria-label="technical-specification-items-list">
            <li>
              {offeringOptions.length === 1 ? (
                <>
                  {t('offering')}
                  <Typography variant="caption" display="block" aria-label="offering-name">
                    {offeringData?.offering_name}
                  </Typography>
                </>
              ) : (
                <Stack gap={1}>
                  <Typography variant="subtitle1">{t('offerings')}</Typography>
                  <FormControl variant="filled" fullWidth>
                    <InputLabel id="offering-select-label">{t('select')}</InputLabel>
                    <Select
                      labelId="offering-select-label"
                      label={t('offerings')}
                      MenuProps={{
                        className: 'select-menu',
                      }}
                      value={selectOffering}
                      onChange={handleOfferingChange}
                    >
                      {offeringOptions?.map(item => (
                        <MenuItem key={item?.value} value={item?.value}>
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}
            </li>
            <li aria-label="order-type-item">
              {t('orderType')}
              <Typography variant="caption" display="block">
                {type}
              </Typography>
            </li>
            <li aria-label="provider-item">
              {t('provider')}
              <Typography variant="caption" display="block">
                {data?.partner_name}
              </Typography>
            </li>
            <li aria-label="sensor-type-item">
              {t('sensorType')}
              <Typography variant="caption" display="block">
                {data?.sensor_type}
              </Typography>
            </li>
            <li aria-label="sensor-item">
              {t('sensor')}
              <Typography variant="caption" display="block">
                {data?.product_name}
              </Typography>
            </li>
            <li aria-label="spatial-resolution-item">
              {t('spatialResolution')}
              <Typography variant="caption" display="block">
                {`${data?.resolution}m`}
              </Typography>
            </li>

            {((data?.bandsList && !isVantor) ||
              offeringData?.configurations?.[0]?.properties?.bands) && (
              <li aria-label="spectral-bands-item">
                {t('spectralBands')}
                <Typography variant="caption" display="block">
                  {bandList}
                </Typography>
              </li>
            )}

            {offeringData?.price !== undefined && (
              <li aria-label="price-item">
                {t('price')}
                <Typography variant="caption" display="block">
                  {t('from', 'From')}
                  {`: $ ${priceNumberFormat(offeringData?.price, true)} `}
                  {offeringData?.price_type ? (
                    <span>{getPriceFormat(offeringData.price_type)}</span>
                  ) : (
                    ''
                  )}
                </Typography>
              </li>
            )}

            {offeringData?.minimum_billable_aoi_size && (
              <li aria-label="min-aoi-size-item">
                {t('minAOIsize')}
                <Typography variant="caption" display="block">
                  {`${offeringData?.minimum_billable_aoi_size} sq. km`}
                </Typography>
              </li>
            )}
            {offeringData?.maximum_platform_aoi_size && (
              <li aria-label="max-aoi-size-item">
                {t('maxAOISize')}
                <Typography variant="caption" display="block">
                  {`${offeringData?.maximum_platform_aoi_size} sq. km`}
                </Typography>
              </li>
            )}
          </ul>
        </Box>
        <Box className="provider-img-detail">
          <CardMedia
            component="img"
            image={data?.properties?.sample_preview ?? ProviderImg.src}
            alt="Provider Image"
          />
          <Box className="provider-img-content">
            <Typography variant="h6">{t('description')}</Typography>
            <Typography
              style={{ whiteSpace: 'pre-wrap' }}
              variant="body1"
              aria-label="description-text"
            >
              <Linkify
                options={{ target: { url: '_blank' }, attributes: { style: { color: 'white' } } }}
              >
                {`${data?.summary ?? ''}`}
              </Linkify>
            </Typography>
          </Box>
        </Box>
        <Box className="provider-product-detail config-card">
          <Box className="provider-quick-actions">
            <Typography variant="h6">{t('quickActions')}</Typography>
            <List aria-label="quick-actions-list">
              {actions.map((item: QuickAction) => item?.url && actionButton(item))}
            </List>
            {isButtonVisible && <Divider sx={{ margin: '16px 0px 0px', borderColor: '#496178' }} />}
            {isPlatformExplore && isButtonVisible && !isProductOrderAbleForExplore && (
              <>
                <Typography variant="body2" className="mt-3">
                  {t('premiumProductPara')}
                </Typography>
              </>
            )}
          </Box>
          {((isButtonVisible && isPlatformExplore) ||
            (isButtonVisible && isPlatformHub && isProductOrderAbleForHub)) && (
            <>
              <ProductLibraryModal
                data={data}
                offeringData={offeringData}
                configurations={configurations}
                type={type}
                isProductOrderAbleForExplore={isProductOrderAbleForExplore}
                isProductOrderAbleForHub={isProductOrderAbleForHub}
              />
              {(isPlatformHub || (isPlatformExplore && isProductOrderAbleForExplore)) &&
                offeringData &&
                offeringData?.configurations?.[0]?.is_standard && (
                  <>
                    <Accordion
                      sx={{ marginTop: '20px' }}
                      className="skywatch-accordian order-parameters"
                    >
                      <AccordionSummary
                        expandIcon={<KeyboardArrowDownOutlinedIcon />}
                        aria-controls="panel2-content"
                        id="panel2-header"
                      >
                        <Box
                          className="accordian-box"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={0.5}
                        >
                          <ArticleOutlinedIcon />
                          <Typography sx={{ fontWeight: '400' }} variant="body1">
                            {t('orderParameters')}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ padding: '0rem 1.25rem 1.25rem 2.5rem !important' }}>
                        <ul className="detail-list" aria-label="technical-specification-items-list">
                          {offeringData?.configurations?.[0]?.properties?.data &&
                            offeringData?.configurations?.[0]?.properties?.data?.length &&
                            offeringData?.configurations?.[0]?.properties?.data?.map(
                              (item: any) => {
                                return (
                                  <>
                                    <li aria-label="order-type-item">
                                      {item?.display?.label}
                                      <Typography variant="caption" display="block">
                                        {item?.value}
                                      </Typography>
                                    </li>
                                  </>
                                );
                              }
                            )}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                    <Divider sx={{ borderColor: '#496178' }} />
                  </>
                )}
            </>
          )}

          {isPlatformHub && isButtonVisible && (
            <Box display="flex" flexDirection="column" marginTop="1rem" gap="1rem">
              {offeringData && offeringData?.on_platform && (
                <Typography>{t('lookingDifferentParameters')}</Typography>
              )}
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  if (offeringData) {
                    const feasibilityData = {
                      offeringId: offeringData?.offering_id,
                      satellite_constellation: `${data?.product_name} - ${type} - ${offeringData?.offering_name}`,
                      product_name: data?.product_name,
                      minBillableAOI: offeringData?.minimum_billable_aoi_size,
                      resolution: data?.resolution,
                      dataType: data?.sensor_type ? data?.sensor_type?.toLowerCase() : 'optical',
                      isTasking: type == 'Tasking',
                    };
                    setFeasibilityRequestData(feasibilityData);
                    router.push(`/product-library/${data?.product_id}/feasibility-request`);
                  }
                }}
              >
                {t('requestFeasibility')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default ProviderDetailTab;
